import React from 'react';
import { Checkbox, FormControl, InputLabel, ListItemText, MenuItem, Select, SelectChangeEvent } from '@mui/material';
import { formatInt } from '../utils/metrics';
import { ALL_CATEGORIES_LABEL, CategorySelection, ServiceCategoryOption, categoryFilterLabel } from '../utils/serviceCategories';

const ALL = '__all__';

export interface CategoryFilterProps {
  options: ServiceCategoryOption[];
  selected: CategorySelection;
  onChange: (next: CategorySelection) => void;
}

/**
 * Multi-select of service categories. "All" is the default; the first pick from "All" narrows to
 * that one category, further picks add to it, and unticking the last one (or ticking every one)
 * goes back to "All". Names in the selection that the options lack are listed so they can be unticked.
 * @example <ServiceCategorySelect options={options} selected={['Physiotherapy']} onChange={setSelected} />
 */
export const ServiceCategorySelect: React.FC<CategoryFilterProps> = ({ options, selected, onChange }) => {
  const extras: ServiceCategoryOption[] = (selected ?? [])
    .filter((name) => !options.some((option) => option.category === name))
    .map((category) => ({ category, latestSessions: 0, staffCount: 0 }));
  const items = [...options, ...extras];
  const names = items.map((item) => item.category);
  const value = selected === null ? names : selected;

  const handleChange = (event: SelectChangeEvent<string[]>) => {
    const raw = event.target.value;
    const next = (typeof raw === 'string' ? raw.split(',') : raw).filter((name) => name !== ALL);
    if ((typeof raw === 'string' ? raw.split(',') : raw).includes(ALL)) {
      onChange(null);
      return;
    }
    if (selected === null) {
      // From "All" the click unticked one name; the user wants just that one.
      const picked = names.filter((name) => !next.includes(name));
      onChange(picked.length === 1 ? picked : null);
      return;
    }
    const ordered = names.filter((name) => next.includes(name));
    const coversEveryOption = options.length > 0 && options.every((option) => ordered.includes(option.category));
    onChange(ordered.length === 0 || coversEveryOption ? null : ordered);
  };

  return (
    <FormControl size="small" sx={{ minWidth: 220, maxWidth: 320 }}>
      <InputLabel id="service-category-select-label">Service categories</InputLabel>
      <Select<string[]>
        labelId="service-category-select-label"
        id="service-category-select"
        label="Service categories"
        multiple
        value={value}
        onChange={handleChange}
        renderValue={() => categoryFilterLabel(selected)}
        SelectDisplayProps={{ 'data-testid': 'service-category-select' } as React.HTMLAttributes<HTMLDivElement>}
        MenuProps={{ PaperProps: { sx: { maxHeight: 420 } } }}
      >
        <MenuItem value={ALL} dense>
          <Checkbox size="small" checked={selected === null} tabIndex={-1} disableRipple />
          <ListItemText primary={ALL_CATEGORIES_LABEL} />
        </MenuItem>
        {items.map((item) => (
          <MenuItem key={item.category} value={item.category} dense>
            <Checkbox size="small" checked={selected === null || selected.includes(item.category)} tabIndex={-1} disableRipple />
            <ListItemText primary={item.category} secondary={`${formatInt(item.latestSessions)} sessions`} />
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};
